import React from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { stockService } from '../../services/api';
import { Intrant, CategorieIntrant, PaginatedResponse } from '@/types';
import { X } from 'lucide-react';

interface IntrantFormProps {
  intrant?: Intrant;
  onClose: () => void;
  onSuccess?: () => void;
}

interface IntrantFormData {
  code: string;
  nom: string;
  description?: string;
  categorie: number;
  unite: string;
  seuil_critique: number;
  seuil_commande: number;
  duree_vie?: number;
}

const IntrantForm: React.FC<IntrantFormProps> = ({ intrant, onClose, onSuccess }) => {
  const queryClient = useQueryClient();

  const { register, handleSubmit, formState: { errors } } = useForm<IntrantFormData>({
    defaultValues: intrant ? {
      code: intrant.code,
      nom: intrant.nom,
      description: intrant.description || '',
      categorie: intrant.categorie,
      unite: intrant.unite,
      seuil_critique: intrant.seuil_critique,
      seuil_commande: intrant.seuil_commande,
      duree_vie: intrant.duree_vie || undefined
    } : {
      seuil_critique: 10,
      seuil_commande: 20,
      unite: ''
    }
  });

  // Récupération des catégories
  const { data: categoriesResponse } = useQuery<PaginatedResponse<CategorieIntrant>>({
    queryKey: ['categories-intrants'],
    queryFn: () => stockService.getCategories().then(res => res.data)
  });
  const categories = categoriesResponse?.results || [];

  // Récupération des choix d'unités
  const { data: uniteChoices } = useQuery<Array<{ value: string; label: string }>>({
    queryKey: ['unite-choices'],
    queryFn: () => stockService.getUniteChoices().then(res => res.data)
  });
  const unites = uniteChoices || [];

  // Mutation pour créer/modifier un intrant
  const intrantMutation = useMutation<Intrant, Error, IntrantFormData>({
    mutationFn: (data) => intrant
      ? stockService.updateIntrant(intrant.id, data).then(res => res.data)
      : stockService.createIntrant(data).then(res => res.data),
    onSuccess: () => {
      // Le parent (StockList) s'occupe de rafraîchir toutes les données du dashboard
      onSuccess?.();
    }
  });

  const onSubmit: SubmitHandler<IntrantFormData> = async (data) => {
    try {
      const payload = {
        ...data,
        duree_vie: data.duree_vie !== undefined && data.duree_vie !== ('' as unknown) ? data.duree_vie : null,
      };
      await intrantMutation.mutateAsync(payload as IntrantFormData);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">
          {intrant ? 'Modifier l\'intrant' : 'Nouvel intrant'}
        </h2>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700" title="Fermer le formulaire">
          <X className="h-6 w-6" />
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Code *
            </label>
            <input
              {...register('code', { required: 'Le code est requis' })}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
              placeholder="Ex: MAT001"
            />
            {errors.code && <p className="text-red-500 text-sm mt-1">{errors.code.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nom *
            </label>
            <input
              {...register('nom', { required: 'Le nom est requis' })}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
              placeholder="Nom de l'intrant"
            />
            {errors.nom && <p className="text-red-500 text-sm mt-1">{errors.nom.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Catégorie *
            </label>
            <select
              {...register('categorie', { required: 'La catégorie est requise' })}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
            >
              <option value="">Sélectionner une catégorie</option>
              {categories.map(categorie => (
                <option key={categorie.id} value={categorie.id}>
                  {categorie.nom}
                </option>
              ))}
            </select>
            {errors.categorie && <p className="text-red-500 text-sm mt-1">{errors.categorie.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Unité *
            </label>
            <select
              {...register('unite', { required: 'L\'unité est requise' })}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
            >
              <option value="">Sélectionner une unité</option>
              {unites.map(unite => (
                <option key={unite.value} value={unite.value}>
                  {unite.label}
                </option>
              ))}
            </select>
            {errors.unite && <p className="text-red-500 text-sm mt-1">{errors.unite.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Seuil critique *
            </label>
            <input
              type="number"
              min="0"
              {...register('seuil_critique', { required: 'Le seuil critique est requis' })}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
            />
            {errors.seuil_critique && <p className="text-red-500 text-sm mt-1">{errors.seuil_critique.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Seuil de commande *
            </label>
            <input
              type="number"
              min="0"
              {...register('seuil_commande', { required: 'Le seuil de commande est requis' })}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
            />
            {errors.seuil_commande && <p className="text-red-500 text-sm mt-1">{errors.seuil_commande.message}</p>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            {...register('description')}
            rows={3}
          className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
            placeholder="Description de l'intrant..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Durée de vie (en jours)
          </label>
          <input
            type="number"
            min="0"
            {...register('duree_vie')}
          className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
            placeholder="Ex: 365 pour 1 an"
          />
        </div>

        {/* Boutons d'action */}
        <div className="flex justify-end space-x-4 pt-6 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={intrantMutation.isLoading}
            className="px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50"
          >
            {intrantMutation.isLoading ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default IntrantForm;